import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { FileText, ArrowRight, X, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui';
import { FileUploader } from './FileUploader';

/** Minimum characters for meaningful AI documentation (matches backend MIN_TRANSCRIPT_CHARS). */
const MIN_CONTENT_CHARS = 10;

interface DocumentUploadPanelProps {
  note: string;
  onNoteChange: (text: string) => void;
  onContinue: () => void;
  onCapture?: () => void; // Optional callback for capturing document
}

export const DocumentUploadPanel: React.FC<DocumentUploadPanelProps> = ({
  note,
  onNoteChange,
  onContinue,
  onCapture,
}) => {
  const { t } = useTranslation();
  const [fileName, setFileName] = useState<string>('');

  const handleFileSelected = useCallback((file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;
      if (content) {
        onNoteChange(content);
        setFileName(file.name);
        toast.success(t('create.fileLoaded', { fileName: file.name }));
      }
    };
    reader.onerror = () => {
      toast.error(t('create.fileReadError'));
    };
    reader.readAsText(file);
  }, [onNoteChange, t]);

  const handleClear = useCallback(() => {
    onNoteChange('');
    setFileName('');
  }, [onNoteChange]);

  return (
    <div className="space-y-4">
      {note ? (
        <>
          {/* Loaded file info */}
          <div className="p-4 bg-indigo-50 border border-indigo-200 rounded-xl">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-indigo-100 rounded-lg">
                  <FileText className="w-5 h-5 text-indigo-600" />
                </div>
                <div>
                  <p className="font-medium text-slate-900">{fileName}</p>
                  <p className="text-sm text-slate-500">
                    {note.length.toLocaleString()} {t('create.characters')}
                  </p>
                </div>
              </div>
              <button
                onClick={handleClear}
                className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Content preview */}
            <div className="p-3 bg-white rounded-lg border border-indigo-100 max-h-48 overflow-y-auto">
              <pre className="text-xs text-slate-600 whitespace-pre-wrap font-mono">
                {note.slice(0, 500)}
                {note.length > 500 && (
                  <span className="text-slate-400">{t('create.moreCharacters', { count: note.length - 500 })}</span>
                )}
              </pre>
            </div>
          </div>

          {/* Short content warning */}
          {note.trim().length > 0 && note.trim().length < MIN_CONTENT_CHARS && (
            <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm">
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <p>{t('create.contentTooShort')}</p>
            </div>
          )}

          {/* Capture Document button */}
          {onCapture && (
            <div className="flex justify-end">
              <Button
                onClick={onCapture}
                variant="gradient"
                size="lg"
                shape="rounded"
              >
                {t('create.captureDocument')}
                <ArrowRight className="w-5 h-5" />
              </Button>
            </div>
          )}
        </>
      ) : (
        <FileUploader
          onFileSelected={handleFileSelected}
          acceptedFormats={['.txt', '.md']}
          maxSizeMB={10}
          label={t('create.dragDropDocument')}
        />
      )}
    </div>
  );
};
